import { TenantType } from 'src/types/enums';

import { rootApi } from '../rootApi';
import { HTTPRequestType } from '../api.types';
import {
  Country,
  Category,
  EntityLabel,
  CreateLabelDto,
  UpdateLabelDto,
  DeleteLabelDto,
  SkillsResponse,
  GetSkillsProps,
  CategoryResponse,
  CurrencyResponse,
  SearchLabelsProps,
  SearchCategoryProps,
  EntityLabelResponse,
  SearchBusinessInfoProps,
  BusinessIDSearchResponse,
  BusinessNameSearchResponse,
} from './utils.types';

export interface TermsOfServiceResponse {
  id: string;
  content: string;
}

export interface RequestTermsOfServiceParams {
  type: string;
  target: TenantType;
  region: string;
}

export const utilsApi = rootApi.injectEndpoints({
  endpoints: (builder) => ({
    getCountries: builder.query<Country[], void>({
      query: () => ({
        url: `/geo/countries`,
        method: HTTPRequestType.GET,
      }),
      transformResponse: (response: Country[]) => {
        const sortedCountries = response.sort((countryA, countryB) => {
          const name_a = countryA.name.toUpperCase();
          const name_b = countryB.name.toUpperCase();

          if (name_a < name_b) return -1;

          if (name_a > name_b) return 1;

          return 0;
        });

        return sortedCountries;
      },
    }),

    searchCategories: builder.query<CategoryResponse, SearchCategoryProps>({
      query: (data) => ({
        url: `/categories?name=${data.name}&page=${data.page}&per_page=${data.per_page}`,
      }),
      transformResponse: (response: CategoryResponse) => ({
        ...response,
        results: response.results.sort((a: Category, b: Category) => (a.name < b.name ? -1 : 1)),
      }),
    }),

    getCurrencies: builder.query<CurrencyResponse, void>({
      query: () => ({
        url: '/currencies',
      }),
    }),

    getSkills: builder.query<SkillsResponse, GetSkillsProps>({
      query: (params) => ({
        url: '/skills',
        params,
      }),
      providesTags: ['Skills'],
    }),

    searchBusinessInfoById: builder.query<BusinessIDSearchResponse, SearchBusinessInfoProps>({
      query: ({ query, countryCode }) => ({
        url: `/business-info/id-search`,
        method: HTTPRequestType.GET,
        params: {
          query,
          country: countryCode,
        },
      }),
    }),
    searchBusinessInfoByName: builder.query<BusinessNameSearchResponse, SearchBusinessInfoProps>({
      query: ({ query, countryCode }) => ({
        url: `/business-info/name-search`,
        method: HTTPRequestType.GET,
        params: {
          query,
          country: countryCode,
        },
      }),
    }),

    searchLabels: builder.query<EntityLabelResponse, SearchLabelsProps>({
      query: (params) => ({
        url: '/entity-labels',
        params,
      }),
      providesTags: (result, error, { type }) => ([{ type: 'EntityLabels', id: type }]),
    }),

    createLabel: builder.mutation<EntityLabel, CreateLabelDto>({
      query: (data) => ({
        url: '/entity-labels',
        method: HTTPRequestType.POST,
        data,
      }),
      invalidatesTags: (result, error, { type }) => ([{ type: 'EntityLabels', id: type }]),
    }),

    updateLabel: builder.mutation<EntityLabel, UpdateLabelDto>({
      query: ({ id, label }) => ({
        url: `/entity-labels/${id}`,
        method: HTTPRequestType.PATCH,
        data: { label },
      }),
      invalidatesTags: (result, error, { type }) => ([{ type: 'EntityLabels', id: type }]),
    }),

    deleteLabel: builder.mutation<{ id: string }, DeleteLabelDto>({
      query: ({ id }) => ({
        url: `/entity-labels/${id}`,
        method: HTTPRequestType.DELETE,
      }),
      invalidatesTags: (result, error, { type }) => ([{ type: 'EntityLabels', id: type }]),
    }),


  }),
});

export const {
  useGetCountriesQuery,
  useSearchCategoriesQuery,
  useLazySearchCategoriesQuery,
  useGetCurrenciesQuery,
  useGetSkillsQuery,

  useLazySearchBusinessInfoByIdQuery,
  useLazySearchBusinessInfoByNameQuery,

  useSearchLabelsQuery,
  useCreateLabelMutation,
  useUpdateLabelMutation,
  useDeleteLabelMutation,
} = utilsApi;
